import { ProgramTypes, programTypes, RDConservationProgramsList } from '@cibo/core'
import { ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import AddOutlined from '@mui/icons-material/AddOutlined'
import DeleteRounded from '@mui/icons-material/DeleteRounded'
import {
  Button,
  FormControlLabel,
  IconButton,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useDebounceCallback } from '@react-hook/debounce'
import { equals } from 'ramda'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useProgramEngagementContext } from '../../../context'
import { useProgramEngagementDetails, useUpdateProgramEngagementDetails } from '../../../queries'

type ConservationProgramsListDetail = {
  traitId: 'conservationPrograms'
  year: number
  result: ConservationProgramListDetailValue
  input: never
}

type ConservationProgramListDetailValue =
  | undefined
  | {
      none?: boolean
      programs?: Record<ProgramTypes, string[]>
    }

type ProgramFormState = { type: ProgramTypes | ''; name: string }

const emptyProgram: ProgramFormState = {
  type: '',
  name: '',
}

const toUiShapeFromDetail = (programs: Record<ProgramTypes, string[]>) => {
  const theresult = Object.entries(programs)
    .filter(([type, names]) => names.length > 0)
    .reduce((acc, [type, names]) => {
      names.forEach(name => {
        acc.push({ type: type as ProgramTypes, name })
      })
      return acc.length > 0 ? acc : [emptyProgram]
    }, [] as ProgramFormState[])
  return theresult
}

const programsListIsValid = (programs?: { name?: string; type?: ProgramTypes }[]) => {
  return (
    programs?.some?.(program => {
      return program.name && program.type
    })
  )
}

export const RDConservationProgramsListEditor = ({
  detailRequirements,
}: ResourceDetailFeatureTaskEditorProps<RDConservationProgramsList>) => {
  const { t } = useTranslation('@cibo/programs/ConservationProgramsListEditor')

  const { engagementId } = useProgramEngagementContext()
  const details = useProgramEngagementDetails<ConservationProgramsListDetail>({
    resourceId: engagementId,
    detailRequirements: [
      {
        traitId: 'conservationPrograms',
        year: detailRequirements[0]?.year,
        dataType: 'workflow',
      },
    ],
  })

  const updateEngagementDetails = useUpdateProgramEngagementDetails<ConservationProgramsListDetail>(
    {
      engagementId: engagementId as string,
    }
  )

  const [hasConservationPrograms, setHasConservationPrograms] = useState(
    details.data?.[0]?.result?.none ? false : !!details.data?.[0]?.result?.programs ? true : null
  )
  const [conservationPrograms, setConservationPrograms] = useState<ProgramFormState[]>([
    emptyProgram,
  ])

  // set initial state based on query response
  useEffect(() => {
    if (details.data?.[0]?.result?.programs) {
      setConservationPrograms(toUiShapeFromDetail(details.data[0].result.programs))
      setHasConservationPrograms(true)
    } else if (details.data?.[0]?.result?.none) {
      setHasConservationPrograms(false)
      setConservationPrograms([emptyProgram])
    }
  }, [details.isFetched])

  const deleteProgramItem = (index: number) => {
    setConservationPrograms(prev => {
      const newPrograms = prev.filter((_, i) => i !== index)
      if (newPrograms.length === 0) {
        persistDetailValue(hasConservationPrograms, [emptyProgram])
        return [emptyProgram]
      }
      persistDetailValue(hasConservationPrograms, newPrograms)
      return newPrograms
    })
  }

  const onChangeInput = (index: number, changes: Partial<ProgramFormState>) => {
    setConservationPrograms(prev => {
      const newPrograms = [...prev]
      newPrograms[index] = {
        type: changes.type || prev[index].type || '',
        name: changes.name === '' ? changes.name : changes.name || prev[index].name,
      }
      if ((changes.type && !prev[index].name) || (changes.name && !prev[index].type)) {
        return newPrograms
      }
      persistDetailValue(hasConservationPrograms, newPrograms)
      return newPrograms
    })
  }

  const persistDetailValue = useDebounceCallback(
    (hasPrograms: boolean | null, programs?: ProgramFormState[]) => {
      const validPrograms = programsListIsValid(programs)
      const sendUpdates = hasPrograms === false || (hasPrograms === true && validPrograms)

      // If all programs are unnamed, an empty update matches the UI with the database state,
      // and prevents the UI from showing an empty program with a "complete" status.
      const sendEmptyUpdate =
        details.data?.[0]?.result?.none !== false && hasPrograms === true && !validPrograms

      engagementId &&
        (sendUpdates || sendEmptyUpdate) &&
        updateEngagementDetails
          .mutateAsync({
            resourceId: engagementId,
            details: [
              {
                traitId: 'conservationPrograms',
                year: detailRequirements[0]?.year as number,
                input: undefined as never,
                result: !hasPrograms
                  ? { none: true }
                  : sendEmptyUpdate
                  ? undefined
                  : {
                      programs: programs?.reduce((acc, program) => {
                        if (program.type && program.name) {
                          acc[program.type] = acc[program.type] || []
                          acc[program.type].push(program.name)
                        }
                        return acc
                      }, {} as Record<ProgramTypes, string[]>),
                    },
              },
            ],
          })
          .catch(() => {
            setHasConservationPrograms(details.data?.[0]?.result?.none ? false : null)
            setConservationPrograms(
              details.data?.[0]?.result?.programs
                ? toUiShapeFromDetail(details.data[0].result.programs)
                : [emptyProgram]
            )
          })
    },
    300
  )

  return (
    <Stack direction="column" spacing={2}>
      <Typography>{t('conservationProgramQuestion')}</Typography>
      <Typography variant="caption">{t('conservationProgramQuestionClarifyingText')}</Typography>
      <RadioGroup
        value={hasConservationPrograms}
        onChange={e => {
          setHasConservationPrograms(e.target.value === 'true')
          e.target.value === 'false' && setConservationPrograms([emptyProgram])
          persistDetailValue(e.target.value === 'true', e.target.value === 'true' ? [] : undefined)
        }}
      >
        <FormControlLabel
          value={false}
          control={<Radio />}
          label={`${t('noConservationPrograms')}`}
          disabled={!equals(conservationPrograms, [emptyProgram])}
        />
        <FormControlLabel
          value={true}
          control={<Radio />}
          label={`${t('yesConservationPrograms')}`}
          disabled={!equals(conservationPrograms, [emptyProgram])}
        />
      </RadioGroup>

      {hasConservationPrograms && (
        <>
          {conservationPrograms.map((vals, index) => (
            <Stack direction="row" spacing={2} justifyContent="space-between" key={index}>
              <Stack width="30%">
                {index === 0 && <Typography>{t('selectProgram')}</Typography>}
                <Select
                  onChange={e => onChangeInput(index, { type: e.target.value as ProgramTypes })}
                  value={vals.type}
                >
                  {programTypes.map(type => (
                    <MenuItem key={type} value={type}>
                      {t(`programType_${type}`)}
                    </MenuItem>
                  ))}
                </Select>
              </Stack>
              <Stack width="60%">
                {index === 0 && <Typography>{t('name')}</Typography>}
                <TextField
                  onChange={e => onChangeInput(index, { name: e.target.value as ProgramTypes })}
                  value={vals.name}
                />
              </Stack>
              <IconButton
                style={{ marginTop: index === 0 ? 24 : 0 }}
                onClick={() => deleteProgramItem(index)}
                disabled={equals(conservationPrograms, [emptyProgram])}
              >
                <DeleteRounded />
              </IconButton>
            </Stack>
          ))}
          <Stack justifyContent="flex-end" direction="row">
            <Button
              onClick={() => {
                setConservationPrograms(prev => [...prev, emptyProgram])
              }}
              startIcon={<AddOutlined />}
              size="small"
            >
              {t('addAnother')}
            </Button>
          </Stack>
        </>
      )}
    </Stack>
  )
}
